'use client';

import { createContext, ReactNode, useContext } from 'react';
import { SiteShop, SiteShopInput } from '@/app/lib/generated/graphql-types';
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from '@/app/lib/hooks/use-editor/useSingleTypeEditor';
import {
    editableHeroIntroFrom,
    heroIntroInputFrom,
} from '@/app/features/shared/intros/hero/types/HeroIntro.schema';
import { notNull } from '@/app/lib/common/not-null';
import {
    SiteShopEditable,
    SiteShopSchema,
} from '@/app/features/sites/shop/types/SiteShop.schema';
import { getSiteShop } from '@/app/features/sites/shop/data-access/queries';
import { updateSiteShop } from '@/app/features/sites/shop/data-access/mutations';
import LoadingSpinner from '@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner';
import FloatingEditorMenu from '@/app/features/shared/__to_be_integrated/FloatingEditorMenu';
import { toggleDraftMode } from '@/app/lib/actions/editing';

const SiteShopEditorContext = createContext<UseSingleTypeEditorType<SiteShopEditable> | undefined>(undefined);

function editableFromSiteShop(site: SiteShop): SiteShopEditable {
    return SiteShopSchema.parse({
        intro: editableHeroIntroFrom(site.intro),
        companion: site.companion,
        relatedInfos: site.relatedInfos.filter(notNull).map((info) => ({
            ...info,
            links: (info.links ?? []).filter(notNull),
        })),
    });
}

async function siteShopInputFrom(editable: SiteShopEditable): Promise<SiteShopInput> {
    return {
        intro: await heroIntroInputFrom(editable.intro),
        companion: editable.companion,
        relatedInfos: editable.relatedInfos.map((info) => info.documentId),
    };
}

export function useSiteShopEditor() {
    const context = useContext(SiteShopEditorContext);
    if (!context) throw new Error('useSiteShopEditor must be used within SiteShopEditorProvider');
    return context;
}

export default function SiteShopEditorProvider({
    children,
    isDraftModeEnabled,
}: {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}) {
    const editor = useSingleTypeEditor<SiteShop, SiteShopEditable, SiteShopInput>({
        schema: SiteShopSchema,
        loadEntity: getSiteShop,
        update: updateSiteShop,
        toEditable: editableFromSiteShop,
        toInput: siteShopInputFrom,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SiteShopEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={() => void editor.discardChanges()}
            />
        </SiteShopEditorContext.Provider>
    );
}
